import * as React from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { parse } from '@pec/aion-ui-core/helpers/querystring';
import { useTypedSelector } from '../../combineReducers';
import { getAddonStorageKey } from '../../helpers/storage';
import { Addons } from '../../types/enums';

type RouteParams = {
  organizationId: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(3),
      gap: `${theme.spacing(4)}px`,
      color: 'var(--text-black)',
      lineHeight: '1.5rem'
    },
    headLine: {
      fontSize: '1.5rem',
      letterSpacing: '0.01125em'
    },
    text: {
      fontWeight: 500
    },
    emailConfirmation: {
      color: 'var(--black)',
      fontSize: 'var(--font-size-14)',
      lineHeight: '1.25rem'
    },
    button: {
      marginLeft: '6px',
      border: '1px solid var(--on-surface-outline-000000-12-)'
    }
  })
);

export const AddonsSuccessScreen: React.FC = () => {
  const { organization } = useTypedSelector(state => state.organization);
  const { search } = useLocation();
  const classes = useStyles();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation();
  const addon = parse(search).addon as Addons;
  const key = getAddonStorageKey(organizationId, addon);
  const selectedProductRef = React.useRef<string>(localStorage.getItem(key));
  const product = selectedProductRef.current && JSON.parse(selectedProductRef.current);

  return (
    <Box display="flex" width="100%" height="100%">
      <Box>
        <div className={classes.contentContainer}>
          <Typography className={classes.headLine} display="block" variant="h6">
            {organization?.name}
          </Typography>
          <Box display="flex" alignItems="center" marginBottom={2}>
            <img
              width={72}
              height={72}
              src={`${process.env.PUBLIC_URL}${product?.itemSrc}`}
              alt={t('subscriber:survey.successPage.productIcon', 'Selected product icon')}
            />
            <Box marginLeft={1}>
              <Typography className={classes.text} variant="h6">
                {t('subscriber:addon.successText', {
                  defaultValue: 'Thank you for purchasing {{value}}!',
                  value: product?.itemName
                })}
              </Typography>
              <Typography className={classes.emailConfirmation}>
                {t('subscriber:survey.successPage.emailConfirmation', 'You will receive a confirmation email shortly.')}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button href={`/organizations/${organizationId}/questionnaire-v2`} variant="contained" color="primary">
              {t('subscriber:survey.successPage.toQuestionnaire', 'Go to Questionnaire')}
            </Button>
            <Button
              href={`/organizations/${organizationId}`}
              color="primary"
              className={classes.button}
              variant="outlined"
            >
              {t('subscriber:survey.successPage.toDashboard', 'Go to my dashboard')}
            </Button>
          </Box>
        </div>
      </Box>
    </Box>
  );
};
